import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Pencil, Trash2, Search, Truck, X, Eye, Download, Upload, BarChart3, AlertTriangle, CheckCircle2, Clock, Calendar, FileText, ScrollText, Filter, ExternalLink, Building2 } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { VEHICLE_TYPES, VEHICLE_SITUATIONS, SITUATION_TONES, FUEL_TYPES } from '../utils/constants';
import { formatCurrency } from '../utils/format';

const emptyForm = {
  fleet_number: '', internal_code: '',
  plate: '', brand: '', model: '', year: new Date().getFullYear(), model_year: '',
  vehicle_type: 'Ônibus', color: '',
  renavam: '', chassis: '',
  situation: 'Ativo', owner_company_id: '',
  technical: { fuel_type: '', passenger_capacity: '', tank_capacity: '', initial_km: 0, current_km: 0 },
  operational: { driver_name: '', garage: '' },
};

function SituationBadge({ value }) {
  const tone = SITUATION_TONES[value] || 'neutral';
  const map = {
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    neutral: 'bg-neutral-200 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  };
  return <span data-testid="vehicle-situation-badge" className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${map[tone]}`}>{value || '-'}</span>;
}


function FleetExpirations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await api.get('/vehicles/expirations');
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setItems(list);
    } catch (err) {
      toast.error('Erro ao carregar vencimentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const enriched = items.map((it) => {
    let daysDiff = null;
    let status = 'ok';
    if (it.expiration_date) {
      const exp = new Date(it.expiration_date + 'T00:00:00');
      const diffTime = exp.getTime() - now.getTime();
      daysDiff = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (daysDiff < 0) {
        status = 'expired';
      } else if (daysDiff <= 30) {
        status = 'near';
      } else {
        status = 'ok';
      }
    }
    return { ...it, daysDiff, status };
  });

  const totalCount = enriched.length;
  const expiredCount = enriched.filter((i) => i.status === 'expired').length;
  const nearCount = enriched.filter((i) => i.status === 'near').length;
  const okCount = enriched.filter((i) => i.status === 'ok').length;

  const uniqueCompanies = Array.from(new Set(enriched.map((i) => i.owner_company_name).filter(Boolean)));

  const filtered = enriched.filter((it) => {
    if (statusFilter !== 'all' && it.status !== statusFilter) return false;
    if (sourceFilter !== 'all' && it.source !== sourceFilter) return false;
    if (companyFilter !== 'all' && it.owner_company_name !== companyFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchPlate = it.vehicle_plate?.toLowerCase().includes(q);
      const matchFleet = it.vehicle_fleet_number?.toString().toLowerCase().includes(q);
      const matchModel = it.vehicle_model?.toLowerCase().includes(q);
      const matchName = it.name?.toLowerCase().includes(q);
      const matchNumber = it.number?.toLowerCase().includes(q);
      const matchComp = it.owner_company_name?.toLowerCase().includes(q);
      const matchCnpj = it.owner_company_cnpj?.toLowerCase().includes(q);
      return matchPlate || matchFleet || matchModel || matchName || matchNumber || matchComp || matchCnpj;
    }
    return true;
  });

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setSourceFilter('all');
    setCompanyFilter('all');
  };

  const hasActiveFilters = search || statusFilter !== 'all' || sourceFilter !== 'all' || companyFilter !== 'all';

  if (loading) {
    return (
      <div className="p-8 text-center text-neutral-500">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand border-r-transparent mb-2"></div>
        <p>Carregando todos os vencimentos da frota...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 pb-8">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div
          onClick={() => setStatusFilter('all')}
          className={`cursor-pointer rounded-xl bg-white dark:bg-neutral-900 border p-4 transition-all hover:shadow-sm ${statusFilter === 'all' ? 'ring-2 ring-brand border-brand' : 'border-neutral-200 dark:border-neutral-800'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Total de Itens</span>
            <div className="h-8 w-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 flex items-center justify-center">
              <Calendar className="h-4 w-4" />
            </div>
          </div>
          <div className="text-2xl font-bold mt-2">{totalCount}</div>
          <p className="text-xs text-neutral-400 mt-0.5">Documentos e licenciamentos</p>
        </div>

        <div
          onClick={() => setStatusFilter('expired')}
          className={`cursor-pointer rounded-xl bg-white dark:bg-neutral-900 border p-4 transition-all hover:shadow-sm ${statusFilter === 'expired' ? 'ring-2 ring-red-500 border-red-500' : 'border-neutral-200 dark:border-neutral-800'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-red-600 uppercase tracking-wider">Vencidos</span>
            <div className="h-8 w-8 rounded-lg bg-red-100 dark:bg-red-950/40 text-red-600 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-red-600 mt-2">{expiredCount}</div>
          <p className="text-xs text-red-500/80 mt-0.5">Exigem renovação imediata</p>
        </div>

        <div
          onClick={() => setStatusFilter('near')}
          className={`cursor-pointer rounded-xl bg-white dark:bg-neutral-900 border p-4 transition-all hover:shadow-sm ${statusFilter === 'near' ? 'ring-2 ring-amber-500 border-amber-500' : 'border-neutral-200 dark:border-neutral-800'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-amber-600 uppercase tracking-wider">Próximos (30 dias)</span>
            <div className="h-8 w-8 rounded-lg bg-amber-100 dark:bg-amber-950/40 text-amber-600 flex items-center justify-center">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-amber-600 mt-2">{nearCount}</div>
          <p className="text-xs text-amber-500/80 mt-0.5">Vencem no próximo mês</p>
        </div>

        <div
          onClick={() => setStatusFilter('ok')}
          className={`cursor-pointer rounded-xl bg-white dark:bg-neutral-900 border p-4 transition-all hover:shadow-sm ${statusFilter === 'ok' ? 'ring-2 ring-emerald-500 border-emerald-500' : 'border-neutral-200 dark:border-neutral-800'}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Em Dia</span>
            <div className="h-8 w-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
          <div className="text-2xl font-bold text-emerald-600 mt-2">{okCount}</div>
          <p className="text-xs text-emerald-500/80 mt-0.5">Vencimento regular</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por placa, frota, tipo, apólice/nº, empresa..."
              className="pl-9"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-44">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="expired">Vencidos ({expiredCount})</SelectItem>
              <SelectItem value="near">Próximos 30d ({nearCount})</SelectItem>
              <SelectItem value="ok">Em dia ({okCount})</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-full md:w-44">
              <SelectValue placeholder="Origem" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              <SelectItem value="document">Documentos do Veículo</SelectItem>
              <SelectItem value="license">Licenciamentos</SelectItem>
            </SelectContent>
          </Select>

          {uniqueCompanies.length > 0 && (
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-full md:w-56">
                <SelectValue placeholder="Empresa Proprietária" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as empresas</SelectItem>
                {uniqueCompanies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {hasActiveFilters && (
            <Button variant="outline" onClick={clearFilters} className="shrink-0">
              <X className="h-4 w-4 mr-1" /> Limpar
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="px-4 py-3 font-medium">Item / Documento</th>
                <th className="px-4 py-3 font-medium">Origem</th>
                <th className="px-4 py-3 font-medium">Veículo / Vínculo</th>
                <th className="px-4 py-3 font-medium">Empresa Proprietária</th>
                <th className="px-4 py-3 font-medium">Data Vencimento</th>
                <th className="px-4 py-3 font-medium">Valor</th>
                <th className="px-4 py-3 font-medium">Situação</th>
                <th className="px-4 py-3 font-medium text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {filtered.map((it) => {
                const targetTab = it.source === 'license' ? 'licenciamentos' : 'documentos';
                let statusBadge = (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800">
                    <CheckCircle2 className="h-3 w-3" /> Em dia {it.daysDiff !== null ? `(${it.daysDiff}d)` : ''}
                  </span>
                );
                if (it.status === 'expired') {
                  statusBadge = (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400 border border-red-200 dark:border-red-800">
                      <AlertTriangle className="h-3 w-3" /> Vencido há {Math.abs(it.daysDiff)}d
                    </span>
                  );
                } else if (it.status === 'near') {
                  statusBadge = (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                      <Clock className="h-3 w-3" /> Vence em {it.daysDiff}d
                    </span>
                  );
                }

                const expFormatted = it.expiration_date
                  ? new Date(it.expiration_date + 'T00:00:00').toLocaleDateString('pt-BR')
                  : '-';

                return (
                  <tr key={it.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${it.source === 'license' ? 'bg-purple-50 text-purple-600 dark:bg-purple-950/30' : 'bg-blue-50 text-blue-600 dark:bg-blue-950/30'}`}>
                          {it.source === 'license' ? <ScrollText className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                        </div>
                        <div>
                          <div className="font-semibold text-neutral-900 dark:text-neutral-100">{it.name || it.type}</div>
                          {it.number && <div className="text-xs text-neutral-500 font-mono">Nº {it.number}</div>}
                          {it.file_name && <div className="text-[11px] text-neutral-400 truncate max-w-[200px]" title={it.file_name}>{it.file_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${it.source === 'license' ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'}`}>
                        {it.source === 'license' ? 'Licenciamento' : 'Documento'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {it.vehicle_id ? (
                        <div>
                          <div className="flex items-center gap-2">
                            {it.vehicle_fleet_number && (
                              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200">
                                Frota {it.vehicle_fleet_number}
                              </span>
                            )}
                            <button
                              onClick={() => navigate(`/frota/${it.vehicle_id}?tab=${targetTab}`)}
                              className="font-mono text-xs font-bold text-brand hover:underline"
                            >
                              {it.vehicle_plate}
                            </button>
                          </div>
                          {it.vehicle_model && <div className="text-xs text-neutral-500 mt-0.5">{it.vehicle_model}</div>}
                        </div>
                      ) : (
                        <span className="text-neutral-400 text-xs">Vínculo Direto com Empresa</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {it.owner_company_name ? (
                        <div>
                          <div className="font-medium text-xs text-neutral-800 dark:text-neutral-200">{it.owner_company_name}</div>
                          {it.owner_company_cnpj && <div className="text-[11px] text-neutral-500 font-mono">CNPJ: {it.owner_company_cnpj}</div>}
                        </div>
                      ) : (
                        <span className="text-neutral-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-neutral-800 dark:text-neutral-200">{expFormatted}</span>
                    </td>
                    <td className="px-4 py-3 font-medium text-xs">
                      {it.value ? formatCurrency(it.value) : (it.cost ? formatCurrency(it.cost) : <span className="text-neutral-400">-</span>)}
                    </td>
                    <td className="px-4 py-3">{statusBadge}</td>
                    <td className="px-4 py-3 text-right">
                      {it.vehicle_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/frota/${it.vehicle_id}?tab=${targetTab}`)}
                          className="h-8 gap-1 text-xs text-brand hover:text-brand hover:bg-brand/10"
                        >
                          <Eye className="h-3.5 w-3.5" /> Ver Veículo
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-neutral-500">
                    <Calendar className="h-8 w-8 mx-auto text-neutral-400 mb-2" />
                    <p className="font-medium">Nenhum vencimento encontrado com os filtros selecionados.</p>
                    {hasActiveFilters && (
                      <Button variant="link" size="sm" onClick={clearFilters} className="mt-2 text-brand">
                        Limpar filtros de busca
                      </Button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Fleet() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'veiculos');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [limit, setLimit] = useState(Number(searchParams.get('limit')) || 10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [situationFilter, setSituationFilter] = useState(searchParams.get('situation') || 'all');
  const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || 'all');
  const [fuelFilter, setFuelFilter] = useState(searchParams.get('fuel') || 'all');
  const location = useLocation();

  // Sync state to URL when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    if (limit !== 10) params.set('limit', limit);
    if (search) params.set('search', search);
    if (situationFilter !== 'all') params.set('situation', situationFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (fuelFilter !== 'all') params.set('fuel', fuelFilter);
    setSearchParams(params, { replace: true });
  }, [page, limit, search, situationFilter, typeFilter, fuelFilter, setSearchParams]);
  const [modalOpen, setModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get('/masters/owner-companies', {params:{limit:100}}).then(res => setCompanies(res.data.items||[])); }, []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importReport, setImportReport] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort_by: 'fleet_number' };
      if (search) params.search = search;
      if (situationFilter !== 'all') params.situation = situationFilter;
      if (typeFilter !== 'all') params.vehicle_type = typeFilter;
      if (fuelFilter !== 'all') params.fuel_type = fuelFilter;
      const { data } = await api.get('/vehicles', { params });
      setItems(data.items);
      setTotal(data.total);
    } catch { toast.error('Erro ao carregar frota'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, limit, situationFilter, typeFilter, fuelFilter]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, owner_company_id: companies[0]?.id || '' }); setModalOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      ...emptyForm, ...v,
      technical: { ...emptyForm.technical, ...(v.technical || {}) },
      operational: { ...emptyForm.operational, ...(v.operational || {}) },
    });
    setModalOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
            const selectedCompany = form.owner_company_id ? companies.find(c => c.id === form.owner_company_id) : null;
      const payload = {
        ...form,
        owner_company_id: form.owner_company_id || null,
        owner_company_name: selectedCompany ? selectedCompany.name : null,
        owner_company_cnpj: selectedCompany ? selectedCompany.cnpj : null,
        year: Number(form.year),
        model_year: form.model_year ? Number(form.model_year) : null,
        technical: {
          ...(form.technical || {}),
          passenger_capacity: form.technical?.passenger_capacity ? Number(form.technical.passenger_capacity) : null,
          tank_capacity: form.technical?.tank_capacity ? Number(form.technical.tank_capacity) : null,
          initial_km: Number(form.technical?.initial_km) || 0,
          current_km: Number(form.technical?.current_km) || 0,
          owner_company_id: form.owner_company_id || null,
          owner_company_name: selectedCompany ? selectedCompany.name : null,
          owner_company_cnpj: selectedCompany ? selectedCompany.cnpj : null,
        },
      };
      if (editing) { await api.put(`/vehicles/${editing.id}`, payload); toast.success('Veículo atualizado'); }
      else { await api.post('/vehicles', payload); toast.success('Veículo cadastrado'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/vehicles/${deleteId}`); toast.success('Veículo excluído'); setDeleteId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const clearFilters = () => { setSituationFilter('all'); setTypeFilter('all'); setFuelFilter('all'); setSearch(''); };

  const doExport = async (fmt) => {
    try {
      const params = { fmt };
      if (situationFilter !== 'all') params.situation = situationFilter;
      if (typeFilter !== 'all') params.vehicle_type = typeFilter;
      const res = await api.get('/vehicles/export/data', { params, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `frota.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Exportação iniciada');
    } catch { toast.error('Erro na exportação'); }
  };

  const doTemplate = async (fmt) => {
    try {
      const res = await api.get('/vehicles/export/template', { params: { fmt }, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `template_frota.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar template'); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/vehicles/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportReport(data);
      toast.success(`${data.created} importados, ${data.failed} erros`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importação');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <PageHeader
        title="Frota"
        subtitle="Gerencie os veículos da empresa"
        action={
          <div className="flex gap-2">
            <Button data-testid="fleet-dashboard-btn" variant="outline" onClick={() => navigate('/frota/dashboard')}>
              <BarChart3 className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="fleet-import-export-btn" variant="outline"><Download className="h-4 w-4 mr-2" /> Import/Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Exportar</DropdownMenuLabel>
                <DropdownMenuItem data-testid="fleet-export-csv" onClick={() => doExport('csv')}>Exportar CSV</DropdownMenuItem>
                <DropdownMenuItem data-testid="fleet-export-xlsx" onClick={() => doExport('xlsx')}>Exportar Excel</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Importar</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => doTemplate('xlsx')}>Baixar template (Excel)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => doTemplate('csv')}>Baixar template (CSV)</DropdownMenuItem>
                <DropdownMenuItem data-testid="fleet-import-file" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-3.5 w-3.5 mr-2" /> Escolher arquivo…
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={doImport} className="hidden" data-testid="fleet-import-input" />
            <Button data-testid="new-vehicle-btn" onClick={openCreate} className="bg-brand hover:bg-[#A81E23] text-white">Novo Veículo</Button>
          </div>
        }
      />

      <div className="flex gap-4 border-b border-neutral-200 dark:border-neutral-800 mb-6 px-4">
        <button
          className={`pb-3 font-medium text-sm border-b-2 ${activeTab === 'veiculos' ? 'border-brand text-brand' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          onClick={() => setActiveTab('veiculos')}
        >
          Veículos
        </button>
        <button
          className={`pb-3 font-medium text-sm border-b-2 ${activeTab === 'expiracoes' ? 'border-brand text-brand' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}
          onClick={() => setActiveTab('expiracoes')}
        >
          Vencimentos
        </button>
      </div>
      
      {activeTab === 'veiculos' ? (
        <div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input data-testid="fleet-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por placa, frota, marca, motorista, chassi..." className="pl-9" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="fleet-type-filter" className="w-full md:w-44"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos tipos</SelectItem>
              {VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={situationFilter} onValueChange={setSituationFilter}>
            <SelectTrigger data-testid="fleet-situation-filter" className="w-full md:w-44"><SelectValue placeholder="Situação" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas situações</SelectItem>
              {VEHICLE_SITUATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fuelFilter} onValueChange={setFuelFilter}>
            <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Combustível" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {FUEL_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-full md:w-36"><SelectValue placeholder="Exibir" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 por pág.</SelectItem>
              <SelectItem value="20">20 por pág.</SelectItem>
              <SelectItem value="50">50 por pág.</SelectItem>
              <SelectItem value="100">100 por pág.</SelectItem>
            </SelectContent>
          </Select>
          {(search || situationFilter !== 'all' || typeFilter !== 'all' || fuelFilter !== 'all') && (
            <Button variant="outline" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Limpar</Button>
          )}
        </div>
      </div>

      {importReport && (
        <div data-testid="import-report" className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-sm">Relatório da última importação</h3>
              <p className="text-xs text-neutral-500 mt-0.5">
                {importReport.total_rows} linhas • <span className="text-emerald-600">{importReport.created} criados</span> • <span className="text-red-600">{importReport.failed} erros</span>
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setImportReport(null)}><X className="h-4 w-4" /></Button>
          </div>
          {importReport.errors?.length > 0 && (
            <ul className="mt-2 text-xs text-red-600 space-y-0.5 max-h-40 overflow-y-auto">
              {importReport.errors.slice(0, 20).map((e, i) => (
                <li key={i}>Linha {e.row} ({e.plate || '?'}): {e.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Placa</th>
                <th className="text-left px-4 py-3 font-medium">Frota</th>
                <th className="text-left px-4 py-3 font-medium">Veículo</th>
                <th className="text-left px-4 py-3 font-medium">Ano</th>
                <th className="text-left px-4 py-3 font-medium">Tipo</th>
                <th className="text-left px-4 py-3 font-medium">Motorista</th>
                <th className="text-left px-4 py-3 font-medium">Situação</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="fleet-table-body" className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={8} className="p-4"><TableSkeleton rows={6} cols={8} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8}><EmptyState icon={Truck} title="Nenhum veículo cadastrado" description="Cadastre o primeiro veículo da sua frota." /></td></tr>
              ) : items.map((v) => (
                <tr key={v.id} className="tr-hover" data-testid={`vehicle-row-${v.plate}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => navigate(`/frota/${v.id}`, { state: { from: location.pathname + location.search } })} className="inline-flex items-center px-2 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800 font-mono text-xs font-semibold hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors">
                      {v.plate}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{v.fleet_number || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{v.model}</div>
                    <div className="text-xs text-neutral-500 font-mono">{v.chassis ? `Chassi: ${v.chassis}` : (v.color || '-')}</div>
                  </td>
                  <td className="px-4 py-3">{v.year}{v.model_year ? `/${String(v.model_year).slice(-2)}` : ''}</td>
                  <td className="px-4 py-3">{v.vehicle_type}</td>
                  <td className="px-4 py-3">
                    {(v.owner_company_name || v.technical?.owner_company_name) ? (
                      <div>
                        <div className="font-medium text-xs text-neutral-800 dark:text-neutral-200">{v.owner_company_name || v.technical?.owner_company_name}</div>
                        {(v.owner_company_cnpj || v.technical?.owner_company_cnpj) && (
                          <div className="text-[11px] text-neutral-500 font-mono">{v.owner_company_cnpj || v.technical?.owner_company_cnpj}</div>
                        )}
                      </div>
                    ) : <span className="text-neutral-400 text-xs">-</span>}
                  </td>
                  <td className="px-4 py-3">{v.operational?.driver_name || v.driver_name || <span className="text-neutral-400">Sem motorista</span>}</td>
                  <td className="px-4 py-3"><SituationBadge value={v.situation || v.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button data-testid={`view-vehicle-${v.plate}`} size="icon" variant="ghost" onClick={() => navigate(`/frota/${v.id}`, { state: { from: location.pathname + location.search } })} title="Detalhes"><Eye className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(v)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(v.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30" title="Excluir"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
            <DialogDescription>Preencha os dados principais. Detalhes completos podem ser editados na página do veículo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5 md:col-span-3 bg-neutral-50 dark:bg-neutral-800/50 p-3.5 rounded-xl border border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center justify-between mb-1">
                <Label className="font-semibold text-neutral-800 dark:text-neutral-200">Empresa Proprietária (CNPJ Vinculado) *</Label>
                <a href="/cadastros/empresas-proprietarias" target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline font-medium">
                  + Gerenciar Empresas / CNPJs
                </a>
              </div>
              <select
                value={form.owner_company_id || ''}
                onChange={e => setForm({ ...form, owner_company_id: e.target.value })}
                className="w-full h-10 rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand dark:border-neutral-800 dark:bg-neutral-950 font-medium"
              >
                <option value="">Selecione o CNPJ / Empresa proprietária...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.cnpj ? `— CNPJ: ${c.cnpj}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-neutral-500 mt-0.5">Define a qual CNPJ o veículo e seus documentos pertencem.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Placa *</Label>
              <Input data-testid="veh-form-plate" required maxLength={10} value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} className="font-mono uppercase" />
            </div>
            <div className="space-y-1.5">
              <Label>Nº da Frota</Label>
              <Input data-testid="veh-form-fleet-number" value={form.fleet_number} onChange={(e) => setForm({ ...form, fleet_number: e.target.value })} placeholder="F-001" />
            </div>
            <div className="space-y-1.5">
              <Label>Código Interno</Label>
              <Input value={form.internal_code} onChange={(e) => setForm({ ...form, internal_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Marca *</Label>
              <Input data-testid="veh-form-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Modelo *</Label>
              <Input data-testid="veh-form-model" required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Cor</Label>
              <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Ano fabricação *</Label>
              <Input required type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Ano modelo</Label>
              <Input type="number" value={form.model_year} onChange={(e) => setForm({ ...form, model_year: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo *</Label>
              <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                <SelectTrigger data-testid="veh-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>RENAVAM</Label>
              <Input value={form.renavam} onChange={(e) => setForm({ ...form, renavam: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Chassi</Label>
              <Input value={form.chassis} onChange={(e) => setForm({ ...form, chassis: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Situação *</Label>
              <Select value={form.situation} onValueChange={(v) => setForm({ ...form, situation: v })}>
                <SelectTrigger data-testid="veh-form-situation"><SelectValue /></SelectTrigger>
                <SelectContent>{VEHICLE_SITUATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Combustível</Label>
              <Select value={form.technical.fuel_type || 'none'} onValueChange={(v) => setForm({ ...form, technical: { ...form.technical, fuel_type: v === 'none' ? '' : v } })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {FUEL_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>KM inicial</Label>
              <Input type="number" value={form.technical.initial_km} onChange={(e) => setForm({ ...form, technical: { ...form.technical, initial_km: e.target.value } })} />
            </div>
            <div className="space-y-1.5">
              <Label>Capacidade passageiros</Label>
              <Input type="number" value={form.technical.passenger_capacity} onChange={(e) => setForm({ ...form, technical: { ...form.technical, passenger_capacity: e.target.value } })} />
            </div>
            <div className="space-y-1.5 md:col-span-3">
              <Label>Motorista responsável</Label>
              <Input data-testid="veh-form-driver" value={form.operational.driver_name} onChange={(e) => setForm({ ...form, operational: { ...form.operational, driver_name: e.target.value } })} placeholder="Nome do motorista" />
            </div>
            <DialogFooter className="md:col-span-3">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button data-testid="veh-form-save" type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir veículo?</AlertDialogTitle>
            <AlertDialogDescription>O registro será marcado como excluído (soft delete). Pode ser restaurado depois.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-vehicle" onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
          </div>
        ) : (
          <FleetExpirations />
        )}
    </div>
  );
}
